import React from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { useBrand, useMarket, useTenantPath } from "@/lib/brand";
import { lbl } from "@/lib/terms";
import { mediaUrl } from "@/lib/api";

/**
 * AgencySell — the tenant's "Sell your property" page: why-choose-us
 * bullets plus a clear, comparable fee table. Entirely data-driven off
 * brand.selling so it works for any Foundation Signature tenant, not
 * just the one it was first written for.
 */
export default function AgencySell() {
  const { slug: urlSlug } = useParams();
  const brand = useBrand();
  const market = useMarket();
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const tenantSlug = urlSlug || brand.tenant_slug || "foundation";
  const s = brand.selling || {};
  const fees = s.fee_options || [];

  const valuationHref = tenantPath("valuation");
  const contactHref = tenantPath("contact");

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={biz.name ? `Sell Your Home, ${biz.name} | ${brand.regions_covered_prose || "Estate Agents"}` : undefined}
        description={s.intro || (market === "us"
          ? `Sell your home with ${biz.name || "a local brokerage"}: free home valuation, professional marketing and clear, competitive commission. You pay at closing.`
          : `Sell your property with ${biz.name || "a local agent"}: free 60-second valuation, professional marketing and clear, competitive fees. No sale, no fee.`)}
        image={brand.assets?.hero_image_url ? mediaUrl(brand.assets.hero_image_url) : undefined}
      />
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="overline">Selling with {biz.name || "us"}</div>
          <h1 className="font-serif-display text-5xl lg:text-6xl text-primary mt-6 tracking-tight leading-[1.05] max-w-3xl">
            {s.headline || `Sell your home with ${biz.name || "a local team"} who actually know your street.`}
          </h1>
          {s.sub && <p className="text-lg text-muted-foreground mt-6 max-w-2xl leading-relaxed">{s.sub}</p>}
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to={valuationHref} data-testid="sell-cta-valuation">
              <Button size="lg" className="h-12 px-7 rounded-sm sharp-btn">
                {lbl("sell_valuation_cta", market)} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to={contactHref}>
              <Button size="lg" variant="outline" className="h-12 px-7 rounded-sm border-primary/25 sharp-btn">
                Talk to us first
              </Button>
            </Link>
          </div>
        </section>

        {s.intro && (
          <section className="border-t border-border bg-secondary/15">
            <div className="mx-auto max-w-4xl px-6 lg:px-10 py-14 text-center">
              <p className="text-lg text-primary/85 leading-relaxed">{s.intro}</p>
            </div>
          </section>
        )}

        {s.why_us?.length > 0 && (
          <section className="border-t border-border py-16 lg:py-24">
            <div className="mx-auto max-w-6xl px-6 lg:px-10">
              <div className="overline text-center">Why sell with us</div>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                {s.why_us.map((w, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-1" />
                    <span className="text-primary/90 leading-relaxed">{w}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {fees.length > 0 && (
          <section className="border-t border-border bg-secondary/15 py-16 lg:py-24">
            <div className="mx-auto max-w-6xl px-6 lg:px-10">
              <div className="text-center">
                <div className="overline">Our fees, your decision</div>
                <h2 className="font-serif-display text-4xl text-primary mt-3">Clear, competitive pricing</h2>
                {s.guarantee && (
                  <p className="text-sm text-primary font-medium mt-4 max-w-xl mx-auto leading-relaxed">{s.guarantee}</p>
                )}
              </div>
              <div className={`mt-12 grid grid-cols-1 ${fees.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-6`}>
                {fees.map((f, i) => (
                  <div
                    key={i}
                    className={`rounded-sm p-7 border ${f.featured ? "border-accent bg-card shadow-sm" : "border-border bg-card"}`}
                  >
                    {f.featured && (
                      <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold mb-3">Most popular</div>
                    )}
                    <div className="font-serif-display text-2xl text-primary">{f.name}</div>
                    <div className="font-serif-display text-3xl text-primary mt-3">{f.price_label}</div>
                    {f.detail && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.detail}</p>}
                    {f.includes?.length > 0 && (
                      <ul className="mt-5 space-y-2.5">
                        {f.includes.map((inc, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-primary/85">
                            <CheckCircle2 className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" /> {inc}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="border-t border-border py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
            <h2 className="font-serif-display text-4xl text-primary">Ready to find out what your home is worth?</h2>
            <p className="text-muted-foreground mt-5 leading-relaxed">
              {market === "us"
                ? "Request a free, no-obligation home valuation (CMA) from a local agent. No pressure, no strings."
                : "Get an instant, HM Land Registry-backed estimate in 60 seconds. No obligation, no pressure."}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to={valuationHref}>
                <Button size="lg" className="h-12 px-7 rounded-sm">
                  <Sparkles className="w-4 h-4 mr-2" />{lbl("free_valuation_cta", market)}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
